import { ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { PageableSortableSearch, SpringPageResponseData } from '@app/shared/searchable.model';
import { finalize, Observable, Subject, switchMap, tap } from 'rxjs';

export abstract class SortableAndPageableTableController<
  ROW,
  SEARCH extends PageableSortableSearch,
  TABLE_COLUMN extends string = string,
> extends RxjsDirective {
  public searchForm: FormGroup<ControlsOf<SEARCH>>;
  public abstract sortBy?: string;
  public abstract displayedColumns: TABLE_COLUMN[];
  public abstract readonly searchFn: (
    search: Partial<SEARCH>,
  ) => Observable<SpringPageResponseData<ROW>>;
  protected abstract cdr: ChangeDetectorRef;
  protected abstract table: MatTable<ROW>;

  public lastSearch: Partial<SEARCH> = {};
  public pageIndex = 0;
  public pageSize = 20;
  public sortDirection?: string = 'DESC';
  public length!: number;
  public isLoading = false;
  public dataSource$: Observable<SpringPageResponseData<ROW>> | undefined;
  public dataSource: ROW[] = [];
  protected readonly filter$ = new Subject<Partial<SEARCH>>();

  constructor() {
    //readonly errorService: ErrorService,
    super();
    this.searchForm = this.createSearchForm();
    this.initFilterStream();
  }

  public abstract createSearchForm(): FormGroup<ControlsOf<SEARCH>>;

  public buildLastSearch(): Partial<SEARCH> {
    const search: Partial<SEARCH> = this.searchForm.value as Partial<SEARCH>;
    search.sortBy = this.sortBy;
    search.sortDirection = this.sortDirection;
    search.unpaged = false;
    search.pageSize = this.pageSize;
    search.pageIndex = this.pageIndex;

    this.lastSearch = search;
    return search;
  }

  public filterWihQueryParams(route: ActivatedRoute) {
    Object.keys(route.snapshot.queryParams).forEach((key) => {
      if (this.searchForm.get(key)) {
        this.searchForm.get(key)?.patchValue(route.snapshot.queryParams[key]);
      }
    });
    this.filter();
  }

  protected initFilterStream() {
    this.register(
      this.filter$
        .pipe(
          tap(() => {
            this.isLoading = true;
            this.cdr.detectChanges();
          }),
          switchMap((search) =>
            this.searchFn(search).pipe(
              finalize(() => {
                setTimeout(() => {
                  this.isLoading = false;
                  this.cdr.detectChanges();
                }, 0);
              }),
            ),
          ),
        )
        .subscribe({
          next: (res: SpringPageResponseData<ROW>) => {
            setTimeout(() => {
              this.dataSource = res.content || [];
              this.pageIndex = Math.max(0, res.page.number ?? 0);
              this.pageSize = res.page.size;
              this.length = res.page.totalElements;
              this.table.renderRows();
              this.cdr.detectChanges();
            }, 0);
          },
          error: (error) => {
            console.error('Error in filter stream:', error);
          },
        }),
    );
  }

  public filter() {
    const search = this.buildLastSearch();
    this.filter$.next(search);
  }

  public deleteFilter() {
    this.searchForm.reset();
    this.filter();
  }

  public onSearch() {
    this.pageIndex = 0;
    this.lastSearch = this.buildLastSearch();
    this.filter();
  }

  public paginate(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.filter();
  }

  public sortChange(event: Sort) {
    if (event.direction) {
      this.sortDirection = event.direction.toUpperCase();
      this.sortBy = event.active;
    } else {
      delete this.sortDirection;
      delete this.sortBy;
    }
    this.filter();
  }

  public updateRow(from: ROW | null, updated: ROW) {
    const refreshTable = () => {
      this.table.renderRows();
      this.cdr.detectChanges();
    };

    if (!from) {
      // Add updated as first datasource item
      this.dataSource.unshift(updated);
      Promise.resolve().then(refreshTable);
      return;
    }

    const index = this.dataSource.indexOf(from);
    if (index !== -1) {
      this.dataSource[index] = updated;
      Promise.resolve().then(refreshTable);
    } else {
      console.warn('Row to update not found in dataSource', from);
    }
  }
}
